import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement, // Required for PolarArea
} from "chart.js";
import { Bar, PolarArea } from "react-chartjs-2";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import "./Chart.scss";
import { useEffect, useState } from "react";
import { ToggleButton } from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement
);

const Chart = (props) => {
  const { labelColor, borderColor, legendColor } = props;

  const [chartData, setChartData] = useState();
  const [active, setActive] = useState("daily");
  const [categorySaleData, setCategorySaleData] = useState([]);
  const rootStyles = getComputedStyle(document.documentElement);

  useEffect(() => {
    const storedSaleData = sessionStorage.getItem("saleDataChart");
    if (storedSaleData) {
      setChartData(JSON.parse(storedSaleData));
    }

    const storedCategorySaleData = sessionStorage.getItem("Home_CategorySale");
    if (storedCategorySaleData) {
      setCategorySaleData(JSON.parse(storedCategorySaleData));
    }
  }, []);

  const handleActive = (event, newActive) => {
    if (newActive !== null) {
      setActive(newActive);
    }
  };

  const getChartData = () => {
    if (!chartData) return { labels: [], datasets: [] };

    let data;
    let labels;
    let purchaseData;
    let saleData;

    switch (active) {
      case "daily":
        data = chartData.DT || [];
        labels = data.map((item) => item.Day_Name);
        purchaseData = data.map((item) => item.PurchaseAmount);
        saleData = data.map((item) => item.SaleAmount);
        break;
      case "monthly":
        data = chartData.DT2 || [];
        labels = data.map((item) => item.Month_Name);
        purchaseData = data.map((item) => item.PurchaseAmount);
        saleData = data.map((item) => item.SaleAmount);
        break;

      case "yearly":
        data = chartData.DT3 || [];
        const currentYear = new Date().getFullYear();
        const cuu = currentYear - 1; // Get the last two digits of the next year
        const nextYearShort = (currentYear).toString().slice(-2); // Get the last two digits of the next year
        const fiscalYear = `${cuu}-${nextYearShort}`;
        labels = [`Year Total (${fiscalYear})`]; // Use dynamic fiscal year
        purchaseData = data.map((item) => item.PurchaseAmount);
        saleData = data.map((item) => item.SaleAmount);
        break;
      default:
        return { labels: [], datasets: [] };
    }

    return {
      labels,
      datasets: [
        {
          maxBarThickness: 15,
          label: "Sale",
          backgroundColor: "rgb(255, 159, 67)",
          borderColor: "transparent",
          data: saleData,
        },
      ],
    };
  };

  const SaleDataOption = {
    indexAxis: "x",
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    elements: {
      bar: {
        borderRadius: {
          topRight: 15,
          topLeft: 15,
        },
      },
    },
    layout: {
      padding: { top: -4 },
    },
    scales: {
      x: {
        grid: {
          drawTicks: false,
          color: borderColor,
        },
        ticks: { color: labelColor },
      },
      y: {
        min: 0,
        grid: {
          display: false,
          color: borderColor,
        },
        ticks: {
          color: labelColor,
          callback: function (value) {
            return value.toLocaleString("en-IN");
          },
        },
      },
    },
    plugins: {
      legend: {
        align: "end",
        position: "top",
        labels: { color: legendColor },
      },
    },
  };

  const dataPolar = {
    labels: categorySaleData.map((item) => item.Category),
    datasets: [
      {
        data: categorySaleData.map((item) => item.SaleAmount),
        backgroundColor: [
          "#FF5733", // Red
          "#33FF57", // Green
          "#3357FF", // Blue
          "#FF33A1", // Pink
          "#FF9633", // Orange
        ],
        borderWidth: 1,
      },
    ],
  };

  const optionsPolar = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        grid: {
          display: false, // Hides the circular grid lines
        },
        angleLines: {
          display: false, // Hides the radial lines
        },
        ticks: {
          display: false, // Hides the tick marks
        },
        pointLabels: {
          display: true, // Keep point labels visible
          color: labelColor,
        },
        pointLabels: {
          display: false, // Show the labels
          color: labelColor,
        },
      },
    },
    plugins: {
      legend: {
        position: "right",
        align: "end",
        labels: {
          padding: 25,
          boxWidth: 9,
          color: legendColor,
          usePointStyle: true
        }
      },
    },
  };

  return (
    <div>
      <div className="chart_main">
        <p className="infoBox2_title">Sale Overview</p>
        <div style={{ display: "flex", marginLeft: "15px" }}>
          <CardHeader
            sx={{
              flexDirection: ["column", "row"],
              alignItems: ["flex-start", "center"],
              "& .MuiCardHeader-action": { mb: 0 },
              "& .MuiCardHeader-content": { mb: [2, 0] },
            }}
            action={
              <ToggleButtonGroup
                exclusive
                value={active}
                onChange={handleActive}
              >
                <ToggleButton value="daily">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="yearly">Yearly</ToggleButton>
              </ToggleButtonGroup>
            }
            className="Chart_ToggleBtn"
          />
        </div>
        <CardContent>
          <Bar data={getChartData()} height={250} options={SaleDataOption} />
        </CardContent>
      </div>

      {categorySaleData?.length !== 0 && (
        <div className="chart_main">
          <div>
            <p className="infoBox2_title">Category Wise Sale Overview</p>
            <CardContent>
              <PolarArea data={dataPolar} height={250} options={optionsPolar} />
            </CardContent>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chart;
